import { Homography } from '../homography';
import type { Size } from '../util';
import { Vector } from './../vector';
import { CalibratedToImage, MeasureToCalibrated } from './coordinate.projection';
import { Geometry3D, Geometry3DCreationMethod, type GeometryConstructor } from './geometry.3d';
import type { GeometryCreationContext, GeometryFactory } from './geometry.factory';
import { calculateFocalLength } from './geometry.util';

const ALIGNMENT_SIZE = 1;

export interface GeometryPitchCreationContext extends GeometryCreationContext {
	pitch: number; // Angle (Radians)
}

export class PitchGeometryFactory implements GeometryFactory {
	create(ctx: GeometryPitchCreationContext): Geometry3D {
		return PitchGeometryFactory.create(ctx);
	}

	static create(ctx: GeometryPitchCreationContext): Geometry3D {
		var creationContext = {
			planeCenter_MRW: null,
			planeNormal_MRW: null,
			planeAxis_MP: null,
			planeOrigin_MRW: null,
			focalLength: -1,
			calibration: null,
			homography: null,
			ctx,
			createdBy: Geometry3DCreationMethod.PITCH,
		};

		if (ctx.calibration) {
			creationContext.focalLength = ctx.calibration.focal;
			creationContext.calibration = ctx.calibration;
		} else {
			creationContext.focalLength = ctx.focalLength || calculateFocalLength(ctx.resolution, ctx.viewAngle);
		}

		creationContext.planeOrigin_MRW = creationContext.planeCenter_MRW = new Vector(0, 0, ctx.distance * -1);
		creationContext.planeNormal_MRW = new Vector(0, Math.sin(ctx.pitch), Math.cos(ctx.pitch));

		creationContext.planeAxis_MP = { x: null, y: null };
		creationContext.planeAxis_MP.x = new Vector(-1, 0, 0);
		creationContext.planeAxis_MP.y = creationContext.planeNormal_MRW.cross(creationContext.planeAxis_MP.x);

		creationContext.homography = PitchGeometryFactory.calculateHomography(creationContext, ctx.resolution);

		return new Geometry3D(creationContext);
	}

	static calculateHomography(ctx: GeometryConstructor, resolution: Size): number[] {
		var alignmentGrid_MP = [
			[0, 0],
			[ALIGNMENT_SIZE, 0],
			[ALIGNMENT_SIZE, ALIGNMENT_SIZE],
			[0, ALIGNMENT_SIZE],
		].map(Vector.create);

		var ProjectMeasureToCalibrated = MeasureToCalibrated.bind(
			null,
			ctx.focalLength,
			ctx.planeOrigin_MRW,
			ctx.planeAxis_MP,
			resolution
		);
		var ProjectCalibratedToImage = CalibratedToImage.bind(null, resolution);

		var alignmentGrid_CI = <Vector[]>alignmentGrid_MP.map(ProjectMeasureToCalibrated);
		var alignmentGrid_IP = <Vector[]>alignmentGrid_CI.map(ProjectCalibratedToImage);
		return Homography.create(alignmentGrid_IP, alignmentGrid_MP);
	}
}
